import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

// Hook to detect dark mode
const useTheme = () => {
    const [isDark, setIsDark] = React.useState(false);

    useEffect(() => {
        const checkTheme = () => {
            setIsDark(document.documentElement.classList.contains('dark'));
        };

        checkTheme();

        // Watch for theme changes
        const observer = new MutationObserver(checkTheme);
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });

        return () => observer.disconnect();
    }, []);

    return isDark;
};

const ThreeParticles = () => {
    const containerRef = useRef(null);
    const rendererRef = useRef(null);
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const particlesRef = useRef(null);
    const linesRef = useRef(null);
    const mouseRef = useRef({ x: 0, y: 0, isActive: false });
    const animationFrameRef = useRef(null);
    const particleDataRef = useRef([]);
    const timeRef = useRef(0);
    const isDark = useTheme();

    useEffect(() => {
        if (!containerRef.current) return;

        const container = containerRef.current;
        const width = container.clientWidth;
        const height = container.clientHeight;

        // Scene setup
        const scene = new THREE.Scene();
        sceneRef.current = scene;

        // Camera setup
        const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
        camera.position.z = 50;
        cameraRef.current = camera;

        // Renderer setup
        const renderer = new THREE.WebGLRenderer({
            antialias: true,
            alpha: true,
            powerPreference: 'high-performance'
        });
        renderer.setSize(width, height);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setClearColor(0x000000, 0);
        container.appendChild(renderer.domElement);
        rendererRef.current = renderer;

        // Particle configuration - reduced for simpler mesh, even fewer in dark mode
        const baseParticleCount = Math.min(Math.floor(width / 8), 250);
        const particleCount = isDark ? baseParticleCount - 30 : baseParticleCount;
        const spreadX = 90;
        const spreadY = 55;
        const spreadZ = 25;

        // Create particles geometry
        const geometry = new THREE.BufferGeometry();
        const positions = new Float32Array(particleCount * 3);
        const colors = new Float32Array(particleCount * 3);
        const sizes = new Float32Array(particleCount);
        const particleData = [];

        // Colors based on theme
        const particleColor = isDark
            ? new THREE.Color(0.95, 0.95, 1.0)
            : new THREE.Color(0.35, 0.35, 0.4);

        for (let i = 0; i < particleCount; i++) {
            const i3 = i * 3;

            // Random 3D positions
            positions[i3] = (Math.random() - 0.5) * spreadX;
            positions[i3 + 1] = (Math.random() - 0.5) * spreadY;
            positions[i3 + 2] = (Math.random() - 0.5) * spreadZ;

            // Store particle data for smooth physics
            particleData.push({
                vx: 0,
                vy: 0,
                vz: 0,
                baseX: positions[i3],
                baseY: positions[i3 + 1],
                baseZ: positions[i3 + 2],
                phase: Math.random() * Math.PI * 2,
                speed: 0.3 + Math.random() * 0.5,
                amplitude: 0.5 + Math.random() * 1.5
            });

            // Color with slight variation
            const colorVariation = 0.9 + Math.random() * 0.2;
            colors[i3] = particleColor.r * colorVariation;
            colors[i3 + 1] = particleColor.g * colorVariation;
            colors[i3 + 2] = particleColor.b * colorVariation;

            // Size based on random
            sizes[i] = 0.4 + Math.random() * 0.5;
        }
        particleDataRef.current = particleData;

        geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

        // Custom shader material for circular particles
        const particleMaterial = new THREE.ShaderMaterial({
            uniforms: {
                uOpacity: { value: isDark ? 0.85 : 0.8 }
            },
            vertexShader: `
        attribute float size;
        attribute vec3 color;
        varying vec3 vColor;
        varying float vOpacity;
        
        void main() {
          vColor = color;
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          
          // Size attenuation
          gl_PointSize = size * (350.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
          
          // Fade based on depth
          vOpacity = 0.5 + (1.0 - (abs(mvPosition.z) / 60.0)) * 0.5;
        }
      `,
            fragmentShader: `
        uniform float uOpacity;
        varying vec3 vColor;
        varying float vOpacity;
        
        void main() {
          // Circular particle shape
          vec2 center = gl_PointCoord - vec2(0.5);
          float dist = length(center);
          
          if (dist > 0.5) discard;
          
          // Soft edge
          float alpha = 1.0 - smoothstep(0.25, 0.5, dist);
          gl_FragColor = vec4(vColor, alpha * uOpacity * vOpacity);
        }
      `,
            transparent: true,
            depthWrite: false,
            blending: THREE.AdditiveBlending
        });

        const particles = new THREE.Points(geometry, particleMaterial);
        scene.add(particles);
        particlesRef.current = particles;

        // Lines for connections - increased visibility
        const lineGeometry = new THREE.BufferGeometry();
        const lineMaterial = new THREE.LineBasicMaterial({
            color: isDark ? 0xcccccc : 0x555555,
            transparent: true,
            opacity: isDark ? 0.10 : 0.18,  // Mesh lines opacity
            blending: THREE.NormalBlending
        });
        const lines = new THREE.LineSegments(lineGeometry, lineMaterial);
        scene.add(lines);
        linesRef.current = lines;

        // Mouse tracking - use window for tracking so it works over text elements
        const handleMouseMove = (event) => {
            const rect = container.getBoundingClientRect();
            // Check if mouse is within the hero section bounds
            if (event.clientX >= rect.left && event.clientX <= rect.right &&
                event.clientY >= rect.top && event.clientY <= rect.bottom) {
                mouseRef.current.x = ((event.clientX - rect.left) / width) * 2 - 1;
                mouseRef.current.y = -((event.clientY - rect.top) / height) * 2 + 1;
                mouseRef.current.isActive = true;
            } else {
                mouseRef.current.isActive = false;
            }
        };

        // Use window-level event to capture mouse over all elements
        window.addEventListener('mousemove', handleMouseMove);

        // Animation loop
        const animate = () => {
            timeRef.current += 0.016; // ~60fps time delta
            const time = timeRef.current;

            const positions = particles.geometry.attributes.position.array;
            const linePositions = [];
            const connectionDistance = 8;  // Reduced for simpler mesh
            const mouseInfluenceRadius = 20;
            const mouseForce = 0.4;

            // Convert mouse to 3D world position
            const mouseWorld = new THREE.Vector3(
                mouseRef.current.x * spreadX / 2,
                mouseRef.current.y * spreadY / 2,
                0
            );

            for (let i = 0; i < particleCount; i++) {
                const i3 = i * 3;
                const data = particleDataRef.current[i];

                // Smooth sinusoidal floating motion (not random)
                const floatX = Math.sin(time * data.speed + data.phase) * data.amplitude * 0.3;
                const floatY = Math.cos(time * data.speed * 0.7 + data.phase) * data.amplitude * 0.4;
                const floatZ = Math.sin(time * data.speed * 0.5 + data.phase * 1.5) * data.amplitude * 0.2;

                // Target position with floating
                let targetX = data.baseX + floatX;
                let targetY = data.baseY + floatY;
                let targetZ = data.baseZ + floatZ;

                // Mouse repulsion
                if (mouseRef.current.isActive) {
                    const dx = positions[i3] - mouseWorld.x;
                    const dy = positions[i3 + 1] - mouseWorld.y;
                    const dz = positions[i3 + 2];
                    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

                    if (distance < mouseInfluenceRadius && distance > 0.1) {
                        const force = (mouseInfluenceRadius - distance) / mouseInfluenceRadius * mouseForce;
                        data.vx += (dx / distance) * force;
                        data.vy += (dy / distance) * force;
                    }
                }

                // Apply velocity with dampening
                data.vx *= 0.92;
                data.vy *= 0.92;
                data.vz *= 0.92;

                // Move towards target with velocity
                positions[i3] += (targetX - positions[i3]) * 0.02 + data.vx;
                positions[i3 + 1] += (targetY - positions[i3 + 1]) * 0.02 + data.vy;
                positions[i3 + 2] += (targetZ - positions[i3 + 2]) * 0.02 + data.vz;

                // Soft boundary constraints
                const boundX = spreadX / 2 + 10;
                const boundY = spreadY / 2 + 10;
                const boundZ = spreadZ / 2 + 5;

                if (Math.abs(positions[i3]) > boundX) {
                    positions[i3] = Math.sign(positions[i3]) * boundX;
                    data.vx *= -0.3;
                }
                if (Math.abs(positions[i3 + 1]) > boundY) {
                    positions[i3 + 1] = Math.sign(positions[i3 + 1]) * boundY;
                    data.vy *= -0.3;
                }
                if (Math.abs(positions[i3 + 2]) > boundZ) {
                    positions[i3 + 2] = Math.sign(positions[i3 + 2]) * boundZ;
                    data.vz *= -0.3;
                }

                // Find connections to nearby particles - more connections
                for (let j = i + 1; j < particleCount; j++) {
                    const j3 = j * 3;
                    const dx = positions[i3] - positions[j3];
                    const dy = positions[i3 + 1] - positions[j3 + 1];
                    const dz = positions[i3 + 2] - positions[j3 + 2];
                    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

                    if (distance < connectionDistance) {
                        linePositions.push(
                            positions[i3], positions[i3 + 1], positions[i3 + 2],
                            positions[j3], positions[j3 + 1], positions[j3 + 2]
                        );
                    }
                }
            }

            // Update particles
            particles.geometry.attributes.position.needsUpdate = true;

            // Update lines
            if (linePositions.length > 0) {
                const lineArray = new Float32Array(linePositions);
                lines.geometry.setAttribute('position', new THREE.BufferAttribute(lineArray, 3));
                lines.geometry.attributes.position.needsUpdate = true;
                lines.visible = true;
            } else {
                lines.visible = false;
            }

            renderer.render(scene, camera);
            animationFrameRef.current = requestAnimationFrame(animate);
        };
        animate();

        // Resize handler
        const handleResize = () => {
            const newWidth = container.clientWidth;
            const newHeight = container.clientHeight;

            camera.aspect = newWidth / newHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(newWidth, newHeight);
        };
        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('mousemove', handleMouseMove);

            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }

            if (rendererRef.current && container.contains(rendererRef.current.domElement)) {
                container.removeChild(rendererRef.current.domElement);
            }

            geometry.dispose();
            particleMaterial.dispose();
            lineGeometry.dispose();
            lineMaterial.dispose();
            renderer.dispose();
        };
    }, [isDark]);

    return (
        <div
            ref={containerRef}
            className="absolute inset-0 pointer-events-none"
            style={{ opacity: isDark ? 0.5 : 0.5 }}
        />
    );
};

export default ThreeParticles;
